/**
 * Maison Beauty Blog Components — JS
 * Handles: product fetch, slider init (tiny-slider), FAQ/TOC interactions
 * Vanilla JS only. No jQuery.
 */

(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // CONFIG
  // --------------------------------------------------------------------------
  const MB = {
    currency: 'EUR',
    locale: 'de-DE',
    ctaText: 'Zum Produkt',
    sliderDefaults: {
      mouseDrag: true,
      touch: true,
      loop: false,
      items: 1,
      gutter: 0,
      controls: false,
      nav: true,
      autoplay: false,
      lazyload: false,
      responsive: {
        640: { items: 2, gutter: 0 },
        1024: { items: 3, gutter: 0 }
      }
    }
  };

  // --------------------------------------------------------------------------
  // UTILITY: Format price from Shopify cents to "12,90 €"
  // --------------------------------------------------------------------------
  function formatPrice(cents) {
    if (cents == null) return '';
    const amount = cents / 100;
    return new Intl.NumberFormat(MB.locale, {
      style: 'currency',
      currency: MB.currency,
      minimumFractionDigits: 2
    }).format(amount);
  }

  // --------------------------------------------------------------------------
  // UTILITY: Fetch a single product by handle
  // Returns a Promise resolving to the product object, or null on error.
  // --------------------------------------------------------------------------
  function fetchProduct(handle) {
    const url = '/products/' + handle.trim() + '.js';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function () {
        return null; // graceful failure
      });
  }

  // --------------------------------------------------------------------------
  // UTILITY: Build product card HTML from Shopify product JSON
  // --------------------------------------------------------------------------
  function buildProductCard(product) {
    if (!product) return null;

    const variant = product.variants && product.variants[0];
    const price = variant ? formatPrice(variant.price) : '';
    const comparePrice =
      variant && variant.compare_at_price && variant.compare_at_price > variant.price
        ? formatPrice(variant.compare_at_price)
        : null;

    // Discount badge
    let badgeHTML = '';
    if (comparePrice && variant.compare_at_price > 0) {
      const pct = Math.round(
        ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
      );
      if (pct > 0) {
        badgeHTML =
          '<span class="mb-blog__product-badge">-' + pct + '%</span>';
      }
    }

    // Image
    const imgSrc =
      product.featured_image ||
      (product.images && product.images[0] ? product.images[0] : '');
    const imgTag = imgSrc
      ? '<img src="' +
        imgSrc +
        '" alt="' +
        escapeHtml(product.title) +
        '" loading="lazy" />'
      : '<div style="width:100%;height:100%;background:var(--mb-primary-light);"></div>';

    // Vendor
    const vendorHTML = product.vendor
      ? '<p class="mb-blog__product-vendor">' + escapeHtml(product.vendor) + '</p>'
      : '';

    // Compare price
    const comparePriceHTML = comparePrice
      ? '<span class="mb-blog__product-compare-price">' +
        escapeHtml(comparePrice) +
        '</span>'
      : '';

    // Product URL
    const productUrl = '/products/' + product.handle;

    return (
      '<div class="mb-blog__product-card" data-status="loaded">' +
        '<div class="mb-blog__product-img-wrap">' +
          imgTag +
        '</div>' +
        '<div class="mb-blog__product-body">' +
          vendorHTML +
          '<p class="mb-blog__product-title">' + escapeHtml(product.title) + '</p>' +
          '<div class="mb-blog__product-price-wrap">' +
            '<span class="mb-blog__product-price">' + escapeHtml(price) + '</span>' +
            comparePriceHTML +
            badgeHTML +
          '</div>' +
          '<a href="' + escapeHtml(productUrl) + '" class="mb-blog__product-cta">' +
            MB.ctaText +
          '</a>' +
        '</div>' +
      '</div>'
    );
  }

  // --------------------------------------------------------------------------
  // UTILITY: Build skeleton card HTML (loading state)
  // --------------------------------------------------------------------------
  function buildSkeletonCard() {
    return (
      '<div class="mb-blog__product-skeleton">' +
        '<div class="mb-blog__skeleton-img"></div>' +
        '<div class="mb-blog__skeleton-body">' +
          '<div class="mb-blog__skeleton-line mb-blog__skeleton-line--title"></div>' +
          '<div class="mb-blog__skeleton-line mb-blog__skeleton-line--price"></div>' +
          '<div class="mb-blog__skeleton-line mb-blog__skeleton-line--btn"></div>' +
        '</div>' +
      '</div>'
    );
  }

  // --------------------------------------------------------------------------
  // UTILITY: Escape HTML to prevent XSS
  // --------------------------------------------------------------------------
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // --------------------------------------------------------------------------
  // PRODUCT SLIDER
  // Reads: data-mb-slider container with [data-products="handle1,handle2,..."]
  // Inserts slider slides and inits tiny-slider.
  // --------------------------------------------------------------------------
  function initProductSliders() {
    var sliders = document.querySelectorAll('[data-mb-slider]');
    sliders.forEach(function (sliderEl) {
      var handles = (sliderEl.getAttribute('data-products') || '')
        .split(',')
        .map(function (h) { return h.trim(); })
        .filter(Boolean);

      if (!handles.length) return;

      var trackEl = sliderEl.querySelector('[data-mb-slider-track]');
      var navEl = sliderEl.querySelector('[data-mb-slider-nav]');
      if (!trackEl) return;

      // Build skeleton slides first
      var skeletonHTML = handles
        .map(function () { return '<div>' + buildSkeletonCard() + '</div>'; })
        .join('');
      trackEl.innerHTML = skeletonHTML;

      // Fetch all products in parallel
      Promise.all(handles.map(fetchProduct)).then(function (products) {
        var slidesHTML = products
          .map(function (product) {
            if (!product) {
              // Hidden placeholder to preserve slider item count
              return '<div style="display:none"></div>';
            }
            return '<div>' + buildProductCard(product) + '</div>';
          })
          .join('');

        trackEl.innerHTML = slidesHTML;

        // Remove hidden slides (unfound products) from DOM
        var hiddenSlides = trackEl.querySelectorAll('[style="display:none"]');
        hiddenSlides.forEach(function (slide) {
          slide.parentNode.removeChild(slide);
        });

        // Guard: need at least 1 visible slide
        var visibleSlides = trackEl.children.length;
        if (visibleSlides === 0) {
          sliderEl.style.display = 'none';
          return;
        }

        // Init tiny-slider only if library is loaded
        if (typeof tns === 'undefined') {
          console.warn('[MB Blog] tiny-slider not loaded. Load it before maison-blog.js.');
          return;
        }

        // Build slider config
        var sliderConfig = Object.assign({}, MB.sliderDefaults, {
          container: trackEl
        });

        // Only use navContainer if it exists and is a valid element
        if (navEl && navEl.nodeType === 1) {
          sliderConfig.navContainer = navEl;
        }

        // Adjust items count if fewer slides than config
        if (visibleSlides < 3) {
          sliderConfig.responsive = {
            640: { items: Math.min(2, visibleSlides), gutter: 0 },
            1024: { items: Math.min(visibleSlides, 3), gutter: 0 }
          };
        }

        try {
          tns(sliderConfig);
        } catch (e) {
          console.warn('[MB Blog] Slider init failed:', e.message);
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // PRODUCT GRID
  // Reads: data-mb-grid container with [data-products="handle1,handle2,..."]
  // --------------------------------------------------------------------------
  function initProductGrids() {
    var grids = document.querySelectorAll('[data-mb-grid]');
    grids.forEach(function (gridEl) {
      var handles = (gridEl.getAttribute('data-products') || '')
        .split(',')
        .map(function (h) { return h.trim(); })
        .filter(Boolean);

      if (!handles.length) return;

      var gridInner = gridEl.querySelector('[data-mb-grid-inner]');
      if (!gridInner) return;

      // Show skeletons
      gridInner.innerHTML = handles
        .map(function () { return buildSkeletonCard(); })
        .join('');

      // Fetch and render
      Promise.all(handles.map(fetchProduct)).then(function (products) {
        var cardsHTML = products
          .map(function (product) {
            if (!product) return ''; // skip unfound products silently
            return buildProductCard(product);
          })
          .join('');

        gridInner.innerHTML = cardsHTML;
      });
    });
  }

  // --------------------------------------------------------------------------
  // TABLE OF CONTENTS — Auto-build from article H2s
  // If [data-mb-toc-auto] is set, the TOC list is populated from H2s in scope.
  // The scope element is found via [data-mb-toc-scope] selector,
  // or defaults to the nearest ancestor article/main.
  // --------------------------------------------------------------------------
  function initTOC() {
    var tocs = document.querySelectorAll('[data-mb-toc]');
    tocs.forEach(function (tocEl) {
      var listEl = tocEl.querySelector('[data-mb-toc-list]');
      if (!listEl) return;

      // Only auto-build if the attribute is present and list is empty
      var isAuto = tocEl.hasAttribute('data-mb-toc-auto');
      if (!isAuto) return;

      // Find scope
      var scopeSelector = tocEl.getAttribute('data-mb-toc-scope');
      var scope = scopeSelector
        ? document.querySelector(scopeSelector)
        : tocEl.closest('article') || tocEl.closest('main') || document.body;

      if (!scope) return;

      // Collect H2s (skip TOC heading itself)
      var headings = scope.querySelectorAll('h2');
      var items = [];
      headings.forEach(function (h2) {
        // Skip if inside the TOC component itself
        if (tocEl.contains(h2)) return;

        // Ensure anchor ID exists
        if (!h2.id) {
          h2.id = slugify(h2.textContent);
        }

        items.push({ id: h2.id, text: h2.textContent.trim() });
      });

      if (!items.length) {
        // Hide TOC if no headings found
        tocEl.style.display = 'none';
        return;
      }

      listEl.innerHTML = items
        .map(function (item) {
          return (
            '<li><a href="#' +
            escapeHtml(item.id) +
            '">' +
            escapeHtml(item.text) +
            '</a></li>'
          );
        })
        .join('');
    });
  }

  // --------------------------------------------------------------------------
  // UTILITY: Slugify for auto-generated IDs
  // --------------------------------------------------------------------------
  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // --------------------------------------------------------------------------
  // FAQ — Schema.org JSON-LD injection
  // Reads all FAQ items and inserts a FAQPage script block if not present.
  // --------------------------------------------------------------------------
  function initFAQSchema() {
    var faqSections = document.querySelectorAll('[data-mb-faq]');
    faqSections.forEach(function (faqEl) {
      if (!faqEl.hasAttribute('data-mb-faq-schema')) return;

      var items = faqEl.querySelectorAll('[data-mb-faq-item]');
      if (!items.length) return;

      var entities = [];
      items.forEach(function (item) {
        var qEl = item.querySelector('[data-mb-faq-question]');
        var aEl = item.querySelector('[data-mb-faq-answer]');
        if (!qEl || !aEl) return;
        entities.push({
          '@type': 'Question',
          name: qEl.textContent.trim(),
          acceptedAnswer: {
            '@type': 'Answer',
            text: aEl.textContent.trim()
          }
        });
      });

      if (!entities.length) return;

      var schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entities
      };

      var scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(scriptEl);
    });
  }

  // --------------------------------------------------------------------------
  // SMOOTH SCROLL for TOC links
  // --------------------------------------------------------------------------
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.mb-blog__toc-list a');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      var target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // --------------------------------------------------------------------------
  // INIT — Run all components on DOMContentLoaded
  // --------------------------------------------------------------------------
  function init() {
    initProductSliders();
    initProductGrids();
    initTOC();
    initFAQSchema();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
